
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FluxoCard } from "@/components/solar/kanban/types";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

// Card color options (same as in CardForm)
const cardColors = [
  { name: "Padrão", value: "bg-card" },
  { name: "Azul", value: "bg-blue-50" },
  { name: "Verde", value: "bg-green-50" },
  { name: "Amarelo", value: "bg-yellow-50" },
  { name: "Laranja", value: "bg-orange-50" },
  { name: "Vermelho", value: "bg-red-50" },
  { name: "Roxo", value: "bg-purple-50" },
  { name: "Rosa", value: "bg-pink-50" },
];

const ExternalCardForm = () => {
  const { funilId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [funis, setFunis] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  
  const [card, setCard] = useState<Partial<FluxoCard>>({
    titulo: "",
    descricao: "",
    status: "",
    prioridade: "media",
    cliente: "",
    telefone: "",
    email: "",
    cor: "bg-card",
    perspective: false
  });
  
  useEffect(() => {
    const loadData = () => {
      setIsLoading(true);
      try {
        const savedFunis = localStorage.getItem("funisProjeto");
        if (savedFunis) {
          const parsedFunis = JSON.parse(savedFunis);
          setFunis(parsedFunis);
          
          // Handle the 'default' parameter specifically
          let targetFunil;
          if (funilId === 'default') {
            // Find the default funil
            targetFunil = parsedFunis.find((f: any) => f.isDefault);
            // If no default is set, use the first one
            if (!targetFunil && parsedFunis.length > 0) {
              targetFunil = parsedFunis[0];
            }
          } else if (funilId) {
            // Find the specific funilId
            targetFunil = parsedFunis.find((f: any) => f.id === funilId);
          } else {
            // No funilId provided, use default or first
            targetFunil = parsedFunis.find((f: any) => f.isDefault) || parsedFunis[0];
          }
            
          if (targetFunil) {
            console.log("Found target funil:", targetFunil.titulo || targetFunil.nome);
            setColumns(targetFunil.colunas);
            // Set default status to first column
            if (targetFunil.colunas.length > 0) {
              setCard(prev => ({ ...prev, status: targetFunil.colunas[0].id }));
            }
          } else {
            console.error("No valid funil found");
          }
        } else {
          console.error("No funis found in localStorage");
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [funilId]);
  
  const handleChange = (field: string, value: any) => {
    setCard({
      ...card,
      [field]: value
    });
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!card.titulo) {
      toast({
        title: "Título obrigatório",
        description: "Por favor, informe um título para o card.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Get current funis data
      const savedFunis = localStorage.getItem("funisProjeto");
      if (savedFunis) {
        const parsedFunis = JSON.parse(savedFunis);
        
        // Find target funil - handle 'default' parameter
        let targetFunilIndex = -1;
        
        if (funilId === 'default') {
          // Find default funil index
          targetFunilIndex = parsedFunis.findIndex((f: any) => f.isDefault);
          
          // If no default found, use first funil
          if (targetFunilIndex === -1 && parsedFunis.length > 0) {
            targetFunilIndex = 0;
          }
        } else if (funilId) {
          // Find specific funil by ID
          targetFunilIndex = parsedFunis.findIndex((f: any) => f.id === funilId);
        } else {
          // No funilId, find default or first
          targetFunilIndex = parsedFunis.findIndex((f: any) => f.isDefault);
          if (targetFunilIndex === -1 && parsedFunis.length > 0) {
            targetFunilIndex = 0;
          }
        }
        
        if (targetFunilIndex >= 0) {
          // Create new card
          const newCard: FluxoCard = {
            id: `card-${Date.now()}`,
            titulo: card.titulo || "",
            descricao: card.descricao || "",
            status: card.status || parsedFunis[targetFunilIndex].colunas[0].id,
            prioridade: card.prioridade || "media",
            cliente: card.cliente || "",
            telefone: card.telefone || "",
            email: card.email || "",
            criadoEm: new Date(),
            cor: card.cor || "bg-card", 
            perspective: card.perspective || false,
            checklistPreenchido: {
              integrador: false,
              unidadeConsumidora: false,
              eletricos: false,
              mecanicos: false,
              financeiros: false,
              documentos: false,
            }
          };
          
          // Find the target column
          const targetColumnIndex = parsedFunis[targetFunilIndex].colunas.findIndex(
            (col: any) => col.id === newCard.status
          );
          
          if (targetColumnIndex >= 0) {
            console.log(`Adding card to column: ${parsedFunis[targetFunilIndex].colunas[targetColumnIndex].titulo}`);
            
            // Add card to column
            parsedFunis[targetFunilIndex].colunas[targetColumnIndex].cards.push(newCard);
            
            // Save updated data
            localStorage.setItem("funisProjeto", JSON.stringify(parsedFunis));
            
            toast({
              title: "Card enviado com sucesso",
              description: "Obrigado pelo envio! Seu card foi adicionado ao fluxo de trabalho."
            });
            
            // Reset form
            setCard({
              titulo: "",
              descricao: "",
              status: columns.length > 0 ? columns[0].id : "",
              prioridade: "media",
              cliente: "",
              telefone: "",
              email: "",
              cor: "bg-card",
              perspective: false
            });
            
            setSubmitted(true);
          } else {
            console.error("Target column not found");
            toast({
              title: "Erro ao salvar",
              description: "Não foi possível encontrar a coluna de destino.",
              variant: "destructive"
            });
          }
        } else {
          console.error("Target funil not found");
          toast({
            title: "Erro ao salvar",
            description: "Não foi possível encontrar o funil especificado.",
            variant: "destructive"
          });
        }
      } else {
        console.error("No funis data in localStorage");
        toast({
          title: "Erro ao salvar",
          description: "Não foram encontrados dados de funis no sistema.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error submitting card:", error);
      toast({
        title: "Erro ao salvar",
        description: "Ocorreu um erro ao salvar o card.",
        variant: "destructive"
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto p-4 max-w-3xl min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Carregando formulário...</h2>
          <p className="text-muted-foreground">Aguarde enquanto carregamos as informações necessárias.</p>
        </div>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="container mx-auto p-4 max-w-3xl min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Enviado com sucesso!</h2>
          <p className="text-muted-foreground mb-8">Seu card foi adicionado ao fluxo de trabalho.</p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Button onClick={() => setSubmitted(false)}>Enviar outro</Button>
            <Button variant="outline" onClick={() => navigate("/")}>Voltar para o Início</Button>
          </div>
        </div>
      </div>
    );
  }
  
  if (columns.length === 0) {
    return (
      <div className="container mx-auto p-4 max-w-3xl min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Formulário não disponível</h2>
          <p className="text-muted-foreground mb-8">Não foi possível encontrar um funil válido para adicionar cards.</p>
          <Button variant="outline" onClick={() => navigate("/")}>Voltar para o Início</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4 max-w-3xl py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold">Formulário de Novo Card</h1>
        <p className="text-muted-foreground">Preencha as informações abaixo para adicionar um novo card ao fluxo de trabalho.</p>
      </div>
      
      <div className="bg-card border rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Título <span className="text-destructive">*</span></Label>
              <Input 
                id="title" 
                value={card.titulo || ""} 
                onChange={(e) => handleChange('titulo', e.target.value)}
                placeholder="Ex: Instalação Residencial"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea 
                id="description" 
                value={card.descricao || ""} 
                onChange={(e) => handleChange('descricao', e.target.value)}
                placeholder="Detalhes sobre o projeto"
                rows={4}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="client">Cliente</Label>
              <Input 
                id="client" 
                value={card.cliente || ''} 
                onChange={(e) => handleChange('cliente', e.target.value)}
                placeholder="Nome do cliente"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="telefone">Telefone</Label>
              <Input 
                id="telefone" 
                value={card.telefone || ''} 
                onChange={(e) => handleChange('telefone', e.target.value)}
                placeholder="Telefone do cliente"
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email"
                value={card.email || ''} 
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Email do cliente"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="status">Status</Label>
                <Select 
                  value={card.status || (columns.length > 0 ? columns[0].id : '')} 
                  onValueChange={(value: any) => handleChange('status', value)}
                >
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Selecione um status" />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((column) => (
                      <SelectItem key={column.id} value={column.id}>
                        {column.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="priority">Prioridade</Label>
                <Select 
                  value={card.prioridade || 'media'} 
                  onValueChange={(value: any) => handleChange('prioridade', value)}
                >
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="Selecione a prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2 mt-2">
              <Label>Aparência do Card</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Cor de Fundo</Label>
                  <RadioGroup 
                    value={card.cor || "bg-card"} 
                    onValueChange={(value) => handleChange('cor', value)}
                    className="grid grid-cols-4 gap-2"
                  >
                    {cardColors.map((color) => (
                      <div key={color.value} className="flex items-center space-x-1">
                        <RadioGroupItem 
                          value={color.value} 
                          id={color.value}
                          className="sr-only"
                        />
                        <Label
                          htmlFor={color.value}
                          className={cn(
                            "h-8 w-8 rounded-md cursor-pointer flex items-center justify-center border hover:ring-2 hover:ring-primary transition-all",
                            color.value
                          )}
                        >
                          {(card.cor || "bg-card") === color.value && (
                            <div className="w-2 h-2 rounded-full bg-primary/70" />
                          )}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center justify-between">
                    <span>Efeito 3D</span>
                    <Switch 
                      checked={card.perspective || false}
                      onCheckedChange={(checked) => handleChange('perspective', checked)}
                    />
                  </Label>
                  <div className="h-16 flex items-center justify-center border rounded-md">
                    <div 
                      className={cn(
                        "w-20 h-10 bg-card border border-muted-foreground/20 rounded shadow-sm flex items-center justify-center text-[8px]",
                        card.perspective ? "transform rotate-y-3 perspective-500" : ""
                      )}
                      style={{
                        transform: card.perspective ? "perspective(500px) rotateY(15deg)" : "",
                        transition: "transform 0.3s ease"
                      }}
                    >
                      Visualização
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button type="submit" className="w-full sm:w-auto">
              Enviar Card
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExternalCardForm;
